import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import WeakPasswordBanner from "../components/WeakPasswordBanner";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Briefcase, Sparkles, FileText, Plus, Users, TrendingUp, ArrowRight } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [apps, setApps] = useState([]);
  const [postedJobs, setPostedJobs] = useState([]);
  const [jobs, setJobs] = useState([]);
  useDocumentTitle("Dashboard");

  useEffect(() => {
    if (user?.role === "admin") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "job_seeker") {
      api.get("/profile/seeker").then(({ data }) => setProfile(data || {})).catch(() => {});
      api.get("/applications/mine").then(({ data }) => setApps(data || [])).catch(() => {});
      api.get("/jobs").then(({ data }) => setJobs((data || []).slice(0, 4))).catch(() => {});
    } else if (user.role === "employer") {
      api.get("/profile/employer").then(({ data }) => setProfile(data || {})).catch(() => {});
      api.get("/jobs/mine").then(({ data }) => setPostedJobs(data || [])).catch(() => {});
    }
  }, [user]);

  if (!user) return null;

  if (user.role === "employer") {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <VerifyEmailBanner />
          <WeakPasswordBanner />
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">employer dashboard</div>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">
                Welcome, {user.name}
              </h1>
              <p className="text-zinc-600 mt-1">
                {profile?.company_name ? `Hiring at ${profile.company_name}` : "Set up your company profile to get started"}
              </p>
            </div>
            <Button
              onClick={() => navigate("/employer/post-job")}
              data-testid="dash-post-job-btn"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Post a job
            </Button>
          </div>

          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate("/employer/candidates")}
              data-testid="dash-browse-candidates-btn"
              className="border-zinc-300"
            >
              <Users className="h-4 w-4 mr-2" /> Browse candidates
            </Button>
          </div>

          {!profile?.company_name && (
            <Card className="p-6 mb-6 border-amber-200 bg-amber-50">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-md bg-amber-100 grid place-items-center text-amber-700">
                  <Briefcase className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-zinc-950">Complete your company profile</h3>
                  <p className="text-sm text-zinc-700 mt-1">Add your company name, website and details so candidates can find and trust you.</p>
                </div>
                <Button onClick={() => navigate("/employer/profile")} data-testid="dash-complete-profile-btn">
                  Set up profile
                </Button>
              </div>
            </Card>
          )}

          {user.status === "pending" && (
            <Card className="p-6 mb-6 border-amber-300 bg-amber-50" data-testid="employer-pending-banner">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-md bg-amber-200 grid place-items-center text-amber-800">
                  <Briefcase className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-zinc-950">Pending admin approval</h3>
                  <p className="text-sm text-zinc-700 mt-1">
                    Your employer account is under review. Once an admin approves it you'll be able to post jobs. This usually happens within 24 hours.
                  </p>
                </div>
              </div>
            </Card>
          )}

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Active jobs</span>
                <Briefcase className="h-4 w-4 text-zinc-400" />
              </div>
              <div className="font-display text-3xl font-black mt-2">{postedJobs.filter(j => j.is_active).length}</div>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Total jobs posted</span>
                <TrendingUp className="h-4 w-4 text-zinc-400" />
              </div>
              <div className="font-display text-3xl font-black mt-2">{postedJobs.length}</div>
            </Card>
            <Card className="p-5 bg-zinc-950 text-white border-zinc-900">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">AI Matching</span>
                <Sparkles className="h-4 w-4 text-emerald-400" />
              </div>
              <div className="font-display text-base mt-2">Score every applicant against your role automatically.</div>
            </Card>
          </div>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold tracking-tight">Your job postings</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/employer/post-job")}>
                <Plus className="h-4 w-4 mr-1" /> New
              </Button>
            </div>
            {postedJobs.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">
                You haven't posted any jobs yet.{" "}
                <Link to="/employer/post-job" className="text-[#002FA7] font-medium">Post your first one →</Link>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {postedJobs.map((j) => (
                  <Link
                    key={j.job_id}
                    to={`/employer/job/${j.job_id}`}
                    className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded transition-colors"
                    data-testid={`employer-job-row-${j.job_id}`}
                  >
                    <div>
                      <div className="font-semibold text-zinc-950 flex items-center gap-2 flex-wrap">
                        {j.title}
                        {j.auto_removed && (
                          <span
                            className="tag-mono text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                            data-testid={`dashboard-auto-removed-${j.job_id}`}
                          >
                            auto removed · no-activity
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5 tag-mono">
                        {j.location} · {j.job_type} · {j.experience_min}–{j.experience_max} yrs
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="tag-mono">{j.required_skills?.length || 0} skills</Badge>
                      <ArrowRight className="h-4 w-4 text-zinc-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </main>
      </div>
    );
  }

  // Job seeker dashboard
  const lastGap = profile?.last_skill_gap;

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <VerifyEmailBanner />
        <WeakPasswordBanner />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">tester dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">
              Hi {user.name?.split(" ")[0]} 👋
            </h1>
            <p className="text-zinc-600 mt-1">Let's get you closer to your next QA role.</p>
          </div>
          <Button onClick={() => navigate("/profile")} variant="outline" data-testid="dash-edit-profile-btn">
            Edit profile
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* 1. Latest Jobs — most prominent so seekers see openings first */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-xl font-bold tracking-tight">Latest Jobs</h2>
                </div>
                <Link to="/jobs" className="text-sm text-[#002FA7] font-medium">See all →</Link>
              </div>
              {jobs.length === 0 ? (
                <div className="text-sm text-zinc-500 py-8 text-center">No jobs posted yet — check back soon.</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {jobs.map((j) => (
                    <Link
                      key={j.job_id}
                      to={`/jobs/${j.job_id}`}
                      className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded transition-colors"
                      data-testid={`dash-latest-job-${j.job_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-zinc-950 truncate">{j.title}</div>
                        <div className="text-xs tag-mono text-zinc-500 truncate">{j.company_name} · {j.location}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* 2. AI Skill Gap Analyzer */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-xl font-bold tracking-tight">AI Skill Gap Analyzer</h2>
                </div>
                {lastGap?.match_score != null && <span className="ai-score-badge">{lastGap.match_score} / 100</span>}
              </div>

              {lastGap ? (
                <>
                  <p className="text-sm text-zinc-600 mb-4">{lastGap.summary}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">strengths</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(lastGap.strengths || []).map((s, i) => (
                          <span key={`str-${i}-${String(s).slice(0, 40)}`} className="tag-mono px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-2">add these next</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(lastGap.missing_skills || []).slice(0,8).map((m, i) => {
                          const skill = typeof m === "string" ? m : m.skill;
                          return (
                            <span key={`ms-${i}-${String(skill).slice(0, 40)}`} className="tag-mono px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded">
                              {skill}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <Button className="mt-5 bg-[#002FA7] hover:bg-[#00227A] text-white" onClick={() => navigate("/profile?tab=skill-gap")} data-testid="rerun-skill-gap-btn">
                    Re-run analysis
                  </Button>
                </>
              ) : (
                <div>
                  <p className="text-zinc-600 mb-4">
                    Tell us your target role and we'll show you exactly which skills will get you hired faster.
                  </p>
                  <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white" onClick={() => navigate("/profile?tab=skill-gap")} data-testid="run-skill-gap-btn">
                    Run analysis <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </Card>

            {/* 3. My Applications */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-xl font-bold tracking-tight">My applications</h2>
                </div>
                <Link to="/jobs" className="text-sm text-[#002FA7] font-medium">Browse jobs →</Link>
              </div>
              {apps.length === 0 ? (
                <div className="text-sm text-zinc-500 py-8 text-center">No applications yet. <Link to="/jobs" className="text-[#002FA7]">Find a role →</Link></div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {apps.map((a) => (
                    <Link
                      key={a.application_id}
                      to={`/jobs/${a.job_id}`}
                      className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded transition-colors"
                      data-testid={`app-row-${a.application_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-zinc-950 truncate">{a.job?.title || "Job"}</div>
                        <div className="text-xs tag-mono text-zinc-500 truncate">{a.job?.company_name}</div>
                      </div>
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-200 flex-shrink-0">{a.status}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6 min-w-0">
            {/* Profile summary — right rail on desktop, bottom on mobile */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-[#002FA7]" />
                <h2 className="font-display text-lg font-bold tracking-tight">Profile</h2>
              </div>
              <div className="space-y-2 text-sm">
                <Row label="Headline" v={profile?.headline} />
                <Row label="Experience" v={profile?.years_experience ? `${profile.years_experience} years` : null} />
                <Row label="Skills" v={profile?.skills?.length ? `${profile.skills.length} listed` : null} />
                <Row label="Resume" v={profile?.resume_filename} />
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => navigate("/profile")} data-testid="goto-profile-btn">
                Complete profile
              </Button>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-900 font-medium text-right">{v || <span className="text-zinc-400">—</span>}</span>
    </div>
  );
}
